import { gql, useLazyQuery, useQuery } from '@apollo/client';
import { useIsMounted } from '@energiebespaarders/hooks';
import { Box, Counter, Flex, Icon, Spinner } from '@energiebespaarders/symbols';
import { Medium, Small } from '@energiebespaarders/symbols/helpers';
import {
  EnergyCircle,
  HomeHeart,
  LeafAngle,
  PiggyBank,
  ThumbsUp,
} from '@energiebespaarders/symbols/icons/solid';
import { margin, themify } from '@energiebespaarders/symbols/styles/mixins';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { BESPAARCHECKS } from '~/components/HomePage/Truster';
import { useIsInitializing } from '~/hooks/useIsInitializing';
import type { dummy_dummy_content_AboutUsMetricsBlockRecord } from '~/types/generated/cms/dummy';
import delimit from '../../utils/delimit';
import { GET_KLANTENVERTELLEN_REVIEWS } from '../KlantenVertellenWidget';
import type { aboutUsMetrics } from '../../types/generated/aboutUsMetrics';
import type { amountBespaarchecks } from '../../types/generated/amountBespaarchecks';
import type { getKlantenVertellenScore } from '../../types/generated/getKlantenVertellenScore';

export const ABOUT_US_METRICS = gql`
  query aboutUsMetrics {
    aboutUsMetrics {
      totalElectricitySaving
      gasMoney
      electricityMoney
      emission
      electricityPerSecond
      moneyPerSecond
      emissionPerSecond
    }
  }
`;

const StyledCounter = styled.div`
  font-size: ${x => x.theme.type.scale[3]};
  font-weight: 600;
  letter-spacing: -0.025em;
  ${margin(2, 't')};
`;

const INCREMENT_DURATION = 1000;

type MetricCounter = 'bespaarchecks' | 'emission' | 'electricity' | 'money' | 'reviewScore';

interface AboutUsMetricsProps {
  block?: dummy_dummy_content_AboutUsMetricsBlockRecord;
}

const AboutUsMetrics: React.FC<AboutUsMetricsProps> = ({ block }) => {
  const { isInitializing } = useIsInitializing();
  const isMounted = useIsMounted();
  const [electricitySaved, setElectricitySaved] = useState(0);
  const [moneySaved, setMoneySaved] = useState(0);
  const [emissionSaved, setEmissionSaved] = useState(0);

  const countersToShow = useMemo(() => {
    const counters = [] as MetricCounter[];
    if (block?.showScans) counters.push('bespaarchecks');
    if (block?.showElectricity) counters.push('electricity');
    if (block?.showEmission) counters.push('emission');
    if (block?.showMoney) counters.push('money');
    if (block?.showReviewScore) counters.push('reviewScore');
    return counters;
  }, [
    block?.showScans,
    block?.showElectricity,
    block?.showEmission,
    block?.showMoney,
    block?.showReviewScore,
  ]);

  const { data: bespaarcheckData, loading: bespaarchecksLoading } = useQuery<amountBespaarchecks>(
    BESPAARCHECKS,
    {
      fetchPolicy: 'network-only',
      skip: isInitializing || block?.showScans === false,
    },
  );
  const { data: metricsData, loading: metricsLoading } = useQuery<aboutUsMetrics>(
    ABOUT_US_METRICS,
    {
      fetchPolicy: 'network-only',
      skip: isInitializing || (block?.showEmission === false && block.showElectricity === false),
    },
  );
  useEffect(() => {
    const metrics = metricsData?.aboutUsMetrics;

    if (metrics) {
      // Set initial counter values from metrics
      if (!electricitySaved) setElectricitySaved(metrics.totalElectricitySaving);
      if (!moneySaved) setMoneySaved(metrics.gasMoney + metrics.electricityMoney);
      if (!emissionSaved) setEmissionSaved(metrics.emission);

      // Increment every second
      const interval = setInterval(() => {
        // eslint-disable-next-line no-param-reassign
        setElectricitySaved(electricitySaved => (electricitySaved += metrics.electricityPerSecond));
        // eslint-disable-next-line no-param-reassign
        setMoneySaved(moneySaved => (moneySaved += metrics.moneyPerSecond));
        // eslint-disable-next-line no-param-reassign
        setEmissionSaved(emissionSaved => (emissionSaved += metrics.emissionPerSecond));
      }, INCREMENT_DURATION);
      return () => {
        clearInterval(interval);
      };
    }
  }, [metricsData]); // eslint-disable-line

  const [fetchScore, { data, loading: scoreLoading }] = useLazyQuery<getKlantenVertellenScore>(
    GET_KLANTENVERTELLEN_REVIEWS,
    { fetchPolicy: 'cache-first' },
  );
  const ratingValue = data?.getKlantenVertellenScore?.last12MonthAverageRating ?? 9.3;

  useEffect(() => {
    if (
      !data?.getKlantenVertellenScore &&
      isMounted() &&
      countersToShow.includes('reviewScore') &&
      !isInitializing
    ) {
      void fetchScore();
    }
  }, [fetchScore, data, isMounted, countersToShow, isInitializing]);

  const color = block?.color?.hex ?? 'black';

  const renderCounter = useCallback(
    (counter: MetricCounter) => {
      switch (counter) {
        case 'bespaarchecks':
          return bespaarchecksLoading ? (
            <Spinner color={color} />
          ) : (
            <>
              <Icon icon={HomeHeart} solid fill={themify(color)} width="3em" />
              <StyledCounter>
                <Counter
                  value={bespaarcheckData?.amountBespaarchecks ?? 60000}
                  formatValue={(n: number) => delimit(n, 0)}
                  duration={INCREMENT_DURATION}
                />
              </StyledCounter>
              <Small>
                <Medium>Woningscans</Medium>
              </Small>
            </>
          );
        case 'electricity':
          return metricsLoading ? (
            <Spinner color={color} />
          ) : (
            <>
              <Icon icon={EnergyCircle} solid fill={themify(color)} width="3em" />
              <StyledCounter>
                <Counter
                  value={electricitySaved}
                  formatValue={(n: number) => delimit(n, 2)}
                  duration={INCREMENT_DURATION}
                />{' '}
                kWh
              </StyledCounter>
              <Small>
                <Medium>Duurzaam opgewekt</Medium>
              </Small>
            </>
          );
        case 'emission':
          return metricsLoading ? (
            <Spinner color={color} />
          ) : (
            <>
              <Icon icon={LeafAngle} solid fill={themify(color)} width="3em" />
              <StyledCounter>
                <Counter
                  value={emissionSaved}
                  duration={INCREMENT_DURATION}
                  formatValue={(n: number) => delimit(n, 2)}
                />{' '}
                kg CO
                <sub>2</sub>
              </StyledCounter>
              <Small>
                <Medium>Uitstoot verminderd</Medium>
              </Small>
            </>
          );
        case 'money':
          return metricsLoading ? (
            <Spinner color={color} />
          ) : (
            <>
              <Icon icon={PiggyBank} solid fill={themify(color)} width="3em" />
              <StyledCounter>
                €{' '}
                <Counter
                  value={moneySaved}
                  formatValue={(n: number) => delimit(n, 2)}
                  duration={INCREMENT_DURATION}
                />
              </StyledCounter>
              <Small>
                <Medium>Bespaard</Medium>
              </Small>
            </>
          );
        case 'reviewScore':
          return scoreLoading ? (
            <Spinner color={color} />
          ) : (
            <>
              <Icon icon={ThumbsUp} solid fill={themify(color)} width="3em" />
              <StyledCounter>
                <Counter value={ratingValue} formatValue={(n: number) => delimit(n, 1)} />
              </StyledCounter>
              <Small>
                <Medium>Klantenvertellen score</Medium>
              </Small>
            </>
          );
      }
    },
    [
      bespaarcheckData?.amountBespaarchecks,
      bespaarchecksLoading,
      color,
      electricitySaved,
      emissionSaved,
      metricsLoading,
      moneySaved,
      ratingValue,
      scoreLoading,
    ],
  );

  return (
    <Flex flexWrap="wrap" mx={-2} textAlign="center" alignItems="center">
      {countersToShow.map((counter, index) => (
        <Box
          key={`${counter}-${index}`}
          width={[1, 1 / Math.floor(countersToShow.length / 2), 1 / countersToShow.length]}
          px={2}
          style={{ color: themify(color) }}
          mb={index < countersToShow.length - 1 ? [5, 5, 0] : 0}
        >
          {renderCounter(counter)}
        </Box>
      ))}
    </Flex>
  );
};

export default AboutUsMetrics;
